import { Button, Flex, IconFont, Text, Tooltip } from '@byecode/ui'
import type { OtherPlatformType } from '@lighthouse/shared'
import { TagIcon, useAtomData, useDynamicTagDisplay } from '@lighthouse/shared'
import cls from 'classnames'
import React from 'react'
import styled from 'styled-components'

import { otherPlatformListAtom } from '@/atoms/application/state'

import {
    AI_INTEGRATION_TYPE_LIST,
    otherPlatformBackgroundColorMap,
    otherPlatformColorMap,
    otherPlatformIconMap,
    otherPlatformNameMap
} from '../constant'
import { openOtherPlatform } from '../OtherPlatformEvent'
import { OtherPlatformTags } from '../Tags'
import type { IntegrationItem, LookType } from '../types'

interface OtherPlatformsProps {
    data: IntegrationItem
    width: string
    activeType?: OtherPlatformType
    lookType: LookType
    readonly?: boolean
    style?: React.CSSProperties
    onSelect?: (type: OtherPlatformType) => void
    onInstall?: (type: OtherPlatformType) => void
}

const SCxInstallButton = styled(Button)`
    position: absolute;
    bottom: 16px;
    right: 16px;
    left: 16px;
    display: none;
    gap: 4px;
`
const SCxOverflow = styled.div`
    position: absolute;
    inset: 0;
    top: 32%;
    display: none;
    z-index: 1;
    border-radius: 12px;
    overflow: hidden;
    background-image: linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 46%);
`

const SCxContainer = styled.div<{ width: string }>`
    position: relative;
    width: ${({ width }) => width};
    box-shadow: 0px 1px 3px rgba(16, 24, 40, 0.1);
    height: 168px;
    cursor: pointer;
    border-radius: 12px;

    &.model::before {
        content: '模型';
        position: absolute;
        right: 0px;
        top: 0px;
        height: 20px;
        padding: 0 6px;
        display: block;
        color: var(--color-theme-6);
        font-size: 10px;
        text-align: center;
        line-height: 20px;
        border-radius: 0 10px 0 12px;
        background-color: var(--color-theme-3);
        z-index: 2;
    }

    &.readonly {
        pointer-events: none;
        box-shadow: unset;
        border: 1px solid var(--color-gray-200);
    }
    &:hover {
        box-shadow: 0px 4px 12px 0px rgba(16, 24, 40, 0.1);

        ${SCxOverflow} {
            display: block;
        }

        ${SCxInstallButton} {
            display: flex;
            z-index: 1;
        }
    }
`

const SCxWrapper = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
    padding: 14px;
    background-color: var(--color-white);
    border-radius: 12px;
    border: 2px solid transparent;
    z-index: 0;
    &.active {
        border: 2px solid var(--color-theme-7);
    }
`

const SCxItem = styled.div`
    font-size: 12px;
    margin-top: 12px;
    line-height: 20px;
    color: var(--color-gray-500);

    &:hover {
        display: block;
    }
`

const SCxHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    height: 40px;
    /* min-width: 258px; */
    gap: 8px;
    flex: 0;
`

const SCxTagIcon = styled(TagIcon)`
    flex-shrink: 0;
    position: relative;
`

const SCxTips = styled.div`
    position: absolute;
    content: '';
    right: -4px;
    top: -4px;
    width: 8px;
    height: 8px;
    border-radius: 100%;
    background-color: var(--color-yellow-500);
`

export const OtherPlatformsItem: React.FunctionComponent<OtherPlatformsProps> = ({
    data,
    width,
    lookType,
    activeType,
    readonly,
    style,
    onSelect,
    onInstall
}) => {
    const otherPlatformList = useAtomData(otherPlatformListAtom)

    const integration = otherPlatformList?.find(v => v.type === data.type)

    const isInstallIntegration = !!integration

    const isShowPerfectTips = isInstallIntegration && !integration?.configurationComplete && lookType === 'alreadyInstall'

    return (
        <SCxContainer
            width={width}
            className={cls({
                // active: activeType === data.type,
                readonly,
                model: AI_INTEGRATION_TYPE_LIST.includes(data.type)
            })}
            onClick={e => {
                e.stopPropagation()
                onSelect?.(data.type)
            }}
            style={style}
        >
            <SCxWrapper
                className={cls({
                    active: activeType === data.type
                    // notInstalled: otherPlatformList?.every(v => v.type !== data.type)
                })}
            >
                {lookType === 'pluginStore' && <SCxOverflow />}
                <SCxHeader>
                    <SCxTagIcon
                        radius={6}
                        size={40}
                        iconSize={24}
                        iconColor={otherPlatformColorMap[data.type]}
                        icon={otherPlatformIconMap[data.type]}
                        background={otherPlatformBackgroundColorMap[data.type]}
                    >
                        {isShowPerfectTips && (
                            <Tooltip title="使用前需先完善授权">
                                <SCxTips />
                            </Tooltip>
                        )}
                    </SCxTagIcon>
                    <Flex direction="column" style={{ flex: 1, gap: 8, overflow: 'hidden' }}>
                        <Text size={14} weight={600} lineHeight="14px">
                            {otherPlatformNameMap[data.type]}
                        </Text>
                        <OtherPlatformTags data={data.tags} />
                    </Flex>
                </SCxHeader>
                <SCxItem>
                    <Text whiteSpace="pre-wrap" style={{ overflow: 'hidden' }}>
                        {data.description}
                    </Text>
                    {lookType === 'pluginStore' &&
                        (integration ? (
                            <SCxInstallButton
                                size="lg"
                                icon={<IconFont size={16} type="TickCircle" color="var(--color-green-500)" />}
                                onClick={() => onInstall?.(data.type)}
                            >
                                已安装
                            </SCxInstallButton>
                        ) : (
                            <SCxInstallButton
                                type="primary"
                                size="lg"
                                icon={<IconFont size={16} type="WidgetAdd" />}
                                onClick={() => onInstall?.(data.type)}
                            >
                                安装
                            </SCxInstallButton>
                        ))}
                </SCxItem>
            </SCxWrapper>
        </SCxContainer>
    )
}
